import React, { useState, useEffect, useCallback } from "react";
import { Button, IconButton, InputAdornment, LinearProgress, Link, Paper, Snackbar, SnackbarContent, TextField, Typography, CircularProgress, Box, Grid, Divider } from "@mui/material";
import { styled } from '@mui/material/styles';
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { useSelector, useDispatch } from "react-redux";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { green } from "@mui/material/colors";
import { Cache } from "aws-amplify/utils";

import { login as loginAction, setNewPassword as setPasswordAction } from "../../actions/auth_actions";
import { AUTH_ERROR_CLEAR } from "../../actions/types";
import * as ROUTES from "../../constants/routes";
import { ReactComponent as SmLogo } from "../Common/Logo/smLogo.svg";
import { ReactComponent as Mountains } from "./Icons/mountains.svg";
import config from "../../config";

// --- Styled Components to perfectly replicate original CSS ---

const RootContainer = styled(Box)(({ theme }) => ({
    flexGrow: 1,
}));

const LoginPaper = styled(Paper)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    [theme.breakpoints.down(740)]: {
        maxWidth: 600,
        minWidth: 600,
    },
    [theme.breakpoints.down('sm')]: { // Corresponds to old 'xs' breakpoint behavior
        minWidth: '100%',
        width: '100%',
    },
}));

const FormContainer = styled(Box)(({ theme }) => ({
    marginLeft: theme.spacing(4),
    marginRight: '27px',
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
        margin: theme.spacing(2),
    },
}));

const MountainsGrid = styled(Grid)(({ theme }) => ({
    backgroundColor: "#FDEFE7",
    borderTopRightRadius: 8,
    borderBottomRightRadius: 8,
    overflow: "hidden",
    display: "flex", // Keep this for alignment
    '& .mountains-svg': {
        width: "105%",
        marginLeft: -1,
        alignSelf: "flex-end",
        marginBottom: -15,
    }
}));


export const Login = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // --- Redux State ---
    const { error:errorMessage, changePassword, currentAuthUser, success } = useSelector((state) => state.auth);

    // --- Local State ---
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [npassword, setNPassword] = useState("");
    const [presumedOrg, setPresumedOrg] = useState(undefined);
    const [showPassword, setShowPassword] = useState(false);
    const [open, setOpen] = useState(false);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const version = `${process.env.REACT_APP_STAGE}@${process.env.REACT_APP_VERSION}${process.env.REACT_APP_BUILD ? `.${parseInt(process.env.REACT_APP_BUILD, 10)}` : ""}`;
    
    // --- Handlers ---
    const handleClose = useCallback(() => {
        setOpen(false);
        dispatch({ type: AUTH_ERROR_CLEAR });
    }, [dispatch]);

    const login = useCallback((e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        setOpen(false);
        dispatch(loginAction({ username: email.trim().toLowerCase(), password: password.trim() }, navigate));
    }, [email, password, dispatch, navigate]);

    const changePass = useCallback((e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        setOpen(false);
        dispatch(setPasswordAction(currentAuthUser, npassword, navigate));
    }, [npassword, currentAuthUser, dispatch, navigate]);

    // --- Effects ---
    useEffect(() => {
        const checkParamsAndCache = async () => {
            const queryParams = new URLSearchParams(window.location.search);
            const tryCache = await Cache.getItem("origin");
            if (tryCache?.organisation) {
                setPresumedOrg(tryCache.organisation);
            } else if (queryParams.get("org")) {
                setPresumedOrg(queryParams.get("org"));
            }
            
            const emailParam = queryParams.get("email");
            const passParam = queryParams.get("pass");
            if (emailParam && passParam) {
                setEmail(emailParam);
                setPassword(decodeURIComponent(passParam));
            }
        };

        checkParamsAndCache();
    }, []);

    useEffect(() => {
        if (errorMessage) {
            if (errorMessage === "Invalid device key given.") {
                window.location.reload();
            } else {
                setOpen(true);
                setError(errorMessage);
                setLoading(false);
            }
        }
    }, [errorMessage]);

    useEffect(() => {
        if (!changePassword) {
            setLoading(false);
        }
    }, [changePassword]);
    
    // --- Derived State for Password Validation ---
    const isRightLength = npassword.length >= 8;
    const hasSpecial = /[/\W|_/g]/.test(npassword);
    const hasNumber = /\d/.test(npassword);
    const hasUpperCase = /[A-Z]/.test(npassword);

  const textFieldProps = {
    fullWidth: true,
    size: "small",
    variant: "outlined",
    InputProps: {
        sx: { fontSize: 14, backgroundColor: "#F5F6F7", '& .MuiOutlinedInput-notchedOutline': { border: 'none' } },
        style: { border: "none" },
    },
    sx: { mt: 0, mb: 0 }
  };

    return (
        <RootContainer>
            <Box sx={{ height: { xs: 2, md: 3 } }}>
                {loading && <LinearProgress />}
            </Box>

            <Grid container spacing={0} direction="column" justifyContent="center" alignItems="center" sx={{ minHeight: "100vh" }}>
                <Grid item sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                    {presumedOrg && presumedOrg !== "skillzminer" ? (
                        <Box component="img" src={`${config.STORAGE_CLOUDFRONT_URL}/logos/${presumedOrg}_appnav_logo.png`}
                            sx={{
                                maxHeight: { sm: 160 },
                                maxWidth: { xs: '80%', sm: '80%' },
                                mt: { xs: '15px', sm: 0 },
                            }}
                        />
                    ) : (
                        <Box sx={{
                        display: 'flex', // Ensures the Box behaves like a container for the SVG
                        justifyContent: 'center', // Example alignment
                        maxHeight: { sm: 160 },
                        width: { xs: '80%', sm: '100%' }, // Apply responsive width to the container
                        mt: { xs: '15px', sm: 0 },
                    }}>
                        <SmLogo style={{ width: '100%', height: 'auto' }} />
                    </Box>
                    )}
                </Grid>
                <Grid item sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                    <LoginPaper>
                        <Grid container spacing={0} direction="row-reverse" justifyContent="center" alignItems="stretch" sx={{ minHeight: 450 }}>
                            <MountainsGrid item size={{ sm: 5 }} sx={{ display: { xs: 'none', sm: 'flex' }}}>
                                <Mountains className="mountains-svg" sx={{width: {sm:"100%"} }}/>
                            </MountainsGrid>
                            <Grid item size={{ xs: 12, sm: 7 }}>
                                {!changePassword ? (
                                    <FormContainer>
                                        <Typography variant="h4" sx={{ fontSize: { xs: 25, sm: 32 } }}>Sign in</Typography>
                                        <Box component="form" onSubmit={login} sx={{ width: "100%" }}>
                                            <Typography variant="body1" sx={{ color: "#202A3E", fontSize: 14, mb: '4px', mt: '18px' }}>Email:</Typography>
                                            <TextField {...textFieldProps} type="email" name="email" autoComplete="email" placeholder="name@email.com" required value={email} onChange={(e) => setEmail(e.target.value)} />

                                            <Typography variant="body1" sx={{ color: "#202A3E", fontSize: 14, mb: '4px', mt: '18px' }}>Password:</Typography>
                                            <TextField {...textFieldProps} type={showPassword ? "text" : "password"} autoComplete="current-password" placeholder="Enter your password" required value={password} onChange={(e) => setPassword(e.target.value)}
                                                InputProps={{ ...textFieldProps.InputProps, endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton onClick={() => setShowPassword(!showPassword)}>{showPassword ? <VisibilityOff /> : <Visibility />}</IconButton>
                                                    </InputAdornment>
                                                )}}
                                            />
                                            <Typography variant="body2" sx={{ color: "#FF6A14", fontSize: 12, textAlign: "right", mt: '16px' }}>
                                                <Link component={RouterLink} to={ROUTES.FORGOT}>Forgotten your password?</Link>
                                            </Typography>
                                            <Box sx={{ position: 'relative' }}>
                                                <Button variant="contained" color="primary" type="submit" fullWidth disabled={loading} sx={{ mt: { xs: 1, sm: 2 }, mb: { xs: '14px', sm: 2 } }}>Sign in</Button>
                                                {loading && <CircularProgress size={24} sx={{ position: 'absolute', top: '50%', left: '50%', marginTop: -12, marginLeft: -12 }} />}
                                            </Box>
                                        </Box>
                                        <Divider />
                                        <Typography variant="body2" sx={{ color: "#5A6375", fontSize: 14, mt: '14px', textAlign: "center" }}>
                                            Don't have an account? <Link component={RouterLink} to={ROUTES.SIGNUP}>Sign Up</Link>
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: "#5A6375", fontSize: 14, mt: '14px', textAlign: "center" }}>
                                            <Link id="youtube-link" href="https://www.youtube.com/watch?v=1u9GtcsA5z4&list=PLaqkddI5TjYUXqGx-0rFFF2vZVTqYc2qp/" target="_blank" rel="noopener noreferrer">
                                            Visit
                                            </Link>
                                            {" "} our Youtube Channel
                                        </Typography>
                                    </FormContainer>
                                ) : (
                                    <FormContainer>
                                        <Box component="form" onSubmit={changePass} sx={{ width: "100%" }}>
                                            <Typography variant="h4" sx={{ fontSize: { xs: 25, sm: 32 } }}>Final Step</Typography>
                                            <Typography variant="body1" sx={{ color: "#5A6375", fontSize: 14, mt: '3px', mb: 2 }}>Please set a new password</Typography>
                                            <Typography variant="body1" sx={{ color: "#202A3E", fontSize: 14, mb: '4px', mt: '18px' }}>Password</Typography>
                                            {npassword.length > 0 && (
                                                <Paper sx={{ mb: "5px", p: 1, backgroundColor: "#faebd7" }}>
                                                    <Grid container>
                                                        {[
                                                            { label: '1 uppercase character', valid: hasUpperCase }, { label: '1 number', valid: hasNumber },
                                                            { label: '1 symbol', valid: hasSpecial }, { label: '8 characters minimum', valid: isRightLength },
                                                        ].map(rule => (
                                                            <Grid item size={{xs:6}} key={rule.label}>
                                                                <Typography variant="body2" sx={{ color: "#5A6375", fontSize: 12, textAlign: "left", display: 'flex', alignItems: 'center' }}>
                                                                    {rule.valid ? <CheckCircleIcon sx={{ color: "#69cc63", mr: 0.5 }} /> : <CancelIcon sx={{ color: "#ff4c4c", mr: 0.5 }} />} {rule.label}
                                                                </Typography>
                                                            </Grid>
                                                        ))}
                                                    </Grid>
                                                </Paper>
                                            )}
                                            <TextField {...textFieldProps} placeholder="New password" type={showPassword ? "text" : "password"} required value={npassword} onChange={(e) => setNPassword(e.target.value)}
                                                InputProps={{ ...textFieldProps.InputProps, endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton onClick={() => setShowPassword(!showPassword)}>{showPassword ? <VisibilityOff /> : <Visibility />}</IconButton>
                                                    </InputAdornment>
                                                )}}
                                            />
                                            <Button variant="contained" color="primary" type="submit" fullWidth sx={{ mt: { xs: 1, sm: 2 }, mb: { xs: '14px', sm: 2 } }}>Change password</Button>
                                        </Box>
                                    </FormContainer>
                                )}

                                {/* --- ADDED: "Powered By" Section --- */}
                                {presumedOrg && presumedOrg !== "skillzminer" && (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <Box component="img"
                                            src={`${config.STORAGE_CLOUDFRONT_URL}/logos/${presumedOrg}_powered_by_logo.png`}
                                            sx={{ height: 40, width: 165, mb: 2 }}
                                        />
                                    </Box>
                                )}

                                <Box sx={{ fontSize: 9, color: "#bbb", textAlign: "right", mt: '3px', mr: 2, mb: '5px' }}>{version}</Box>
                            </Grid>
                        </Grid>
                    </LoginPaper>
                </Grid>
            </Grid>

            <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={open} autoHideDuration={3000} onClose={handleClose} sx={{ mt: '10px' }}>
                <SnackbarContent message={<span>{error}</span>} sx={{ backgroundColor: (theme) => theme.palette.error.dark }} />
            </Snackbar>
            <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={!!success} autoHideDuration={3000} onClose={handleClose} sx={{ mt: '10px' }}>
                <SnackbarContent message={<span>{success}</span>} sx={{ backgroundColor: green[600] }} />
            </Snackbar>
        </RootContainer>
    );
};